import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllGoals, getCurrentQuarter, deleteGoal } from '../../services/goalsService';
import { getAllManagers, getAllTeamAssignments } from '../../services/userService';
import { GoalDocument, RoleDocument, TeamAssignment } from '../../types';
import { 
  Code2,
  Search,
  Filter,
  Download,
  Users,
  Database,
  CheckSquare,
  Square,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { generateGoalsDocx } from '../../services/docxService';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  initial_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  final_review: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting Review',
  initial_review: 'Under Review',
  approved: 'Approved',
  final_review: 'Final Review',
  completed: 'Completed',
};

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [allGoals, setAllGoals] = useState<GoalDocument[]>([]);
  const [managers, setManagers] = useState<RoleDocument[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedManagers, setSelectedManagers] = useState<Set<string>>(new Set());
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<number | 'all'>('all');
  
  const { quarter, year } = getCurrentQuarter();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [goals, mgrs, teams] = await Promise.all([
        getAllGoals(),
        getAllManagers(),
        getAllTeamAssignments(),
      ]);
      setAllGoals(goals);
      setManagers(mgrs);
      setTeamAssignments(teams);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleManager = (email: string) => {
    const newSelected = new Set(selectedManagers);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedManagers(newSelected);
  };

  const selectAllManagers = () => {
    setSelectedManagers(new Set(managers.map(m => m.email)));
  };

  const deselectAllManagers = () => {
    setSelectedManagers(new Set());
  };

  const filteredGoals = allGoals.filter(goal => {
    const matchesSearch = searchTerm === '' || 
      goal.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      goal.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesManager = selectedManagers.size === 0 || selectedManagers.has(goal.managerEmail);
    const matchesStatus = selectedStatus === 'all' || goal.status === selectedStatus;
    const matchesYear = selectedYear === 'all' || goal.year === selectedYear;
    const matchesQuarter = selectedQuarter === 'all' || goal.quarter === selectedQuarter;
    
    return matchesSearch && matchesManager && matchesStatus && matchesYear && matchesQuarter;
  });

  const years = [...new Set(allGoals.map(g => g.year))].sort((a, b) => b - a);

  const handleDownload = async (goal: GoalDocument) => {
    try {
      await generateGoalsDocx(goal);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const handleDelete = async (goal: GoalDocument) => {
    if (!goal.id) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete goals for ${goal.userName} (Q${goal.quarter} ${goal.year})?\n\nThis cannot be undone.`
    );
    
    if (confirmed) {
      try {
        await deleteGoal(goal.id);
        setAllGoals(prev => prev.filter(g => g.id !== goal.id));
      } catch (error) {
        console.error('Error deleting goal:', error);
        alert('Failed to delete goal. Check console for details.');
      }
    }
  };

  const goalsByManager = filteredGoals.reduce((acc, goal) => {
    const key = goal.managerEmail;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(goal);
    return acc;
  }, {} as Record<string, GoalDocument[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Developer Tools</h1>
          <p className="text-gray-500">Debug view with advanced filtering</p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Data
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-orange-600" />
          <h2 className="text-lg font-semibold text-gray-900">Database Stats</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Total Goals</p>
            <p className="text-xl font-bold text-gray-900">{allGoals.length}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Managers</p>
            <p className="text-xl font-bold text-gray-900">{managers.length}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Team Assignments</p>
            <p className="text-xl font-bold text-gray-900">{teamAssignments.length}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Current Quarter</p>
            <p className="text-xl font-bold text-gray-900">Q{quarter} {year}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter by Manager</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAllManagers}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={deselectAllManagers}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {managers.map(manager => (
            <button
              key={manager.email}
              onClick={() => toggleManager(manager.email)}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                selectedManagers.has(manager.email)
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              {selectedManagers.has(manager.email) ? (
                <CheckSquare className="w-4 h-4 text-blue-600" />
              ) : (
                <Square className="w-4 h-4 text-gray-400" />
              )}
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{manager.displayName}</p>
                <p className="text-xs text-gray-500 capitalize">{manager.role}</p>
              </div>
            </button>
          ))}
        </div>
        {selectedManagers.size > 0 && (
          <p className="mt-3 text-sm text-gray-500">
            {selectedManagers.size} manager(s) selected
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Additional Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Years</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Quarters</option>
              <option value={1}>Q1</option>
              <option value={2}>Q2</option>
              <option value={3}>Q3</option>
              <option value={4}>Q4</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filtered Results</h2>
            </div>
            <span className="text-sm text-gray-500">{filteredGoals.length} goals</span>
          </div>
        </div>
        
        <div className="divide-y max-h-[600px] overflow-auto">
          {Object.entries(goalsByManager).map(([managerEmail, goals]) => {
            const manager = managers.find(m => m.email === managerEmail);
            return (
              <div key={managerEmail} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {manager?.displayName || managerEmail}
                  </span>
                  <span className="text-xs text-gray-400 font-mono">{managerEmail}</span>
                  <span className="text-sm text-gray-400">({goals.length})</span>
                </div>
                <div className="space-y-2 ml-6">
                  {goals.map(goal => (
                    <div 
                      key={goal.id}
                      className="p-3 bg-gray-50 rounded-lg flex items-center justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{goal.userName}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[goal.status]}`}>
                            {statusLabels[goal.status]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Q{goal.quarter} {goal.year} • {goal.userEmail}
                        </p>
                        <p className="text-xs text-gray-400 font-mono mt-1">ID: {goal.id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(goal)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          title="Download DOCX"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {goal.status === 'submitted' && (
                          <button
                            onClick={() => navigate(`/goals/${goal.id}/review`)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Review
                          </button>
                        )}
                        {goal.status === 'approved' && (
                          <button
                            onClick={() => navigate(`/goals/${goal.id}/final-review`)}
                            className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            Final Review
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/goals/${goal.id}`)}
                          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(goal)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Goal"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          
          {filteredGoals.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No goals found matching your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperDashboard;
